// reducers/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { showError, showSuccess } from "../../helpers/toast";
import {
  getMfaDeviceToken,
  saveMfaDeviceToken,
} from "../../helpers/mfaDeviceToken";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

/**
 * Live MFA contract (apiportal) — SMS_MFA Frontend API Changes:
 * POST /auth/login       { emailOrPhone, password, mfa_device_token? }
 * POST /auth/mfa/verify  { mfa_token, code, mfa_device_token? }
 * POST /auth/mfa/resend  { mfa_token }
 * Persist mfa_device_token from verify for 30-day trusted device (Reviewer/Viewer/etc).
 */
const MFA_VERIFY_PATH =
  process.env.REACT_APP_MFA_VERIFY_PATH || "/auth/mfa/verify";
const MFA_RESEND_PATH =
  process.env.REACT_APP_MFA_RESEND_PATH || "/auth/mfa/resend";

const persistDeviceTokenFromSession = (data, fallbackEmail) => {
  const token = data?.mfa_device_token;
  if (!token) return;
  const email =
    data?.user?.email ||
    fallbackEmail ||
    null;
  saveMfaDeviceToken(
    email,
    token,
    data?.mfa_device_expires_at || data?.mfa_device_expiresAt || null
  );
};

/** Detect MFA challenge from login (no full access_token yet) */
export const isMfaLoginChallenge = (data) => {
  if (!data || typeof data !== "object") return false;
  if (data.access_token && data.user) return false;
  if (
    data.mfa_required === true ||
    data.mfaRequired === true ||
    data.requires_otp === true ||
    data.requiresOtp === true ||
    data.otp_required === true ||
    data.otpRequired === true
  ) {
    return true;
  }
  if (data.mfa_token && !data.access_token) return true;
  const token =
    data.mfaToken ||
    data.session_token ||
    data.otp_token ||
    data.second_factor_token ||
    data.temp_token;
  if (token && !data.access_token) return true;
  return false;
};

const resolveCodeExpiresAt = (data) => {
  if (data?.code_expires_at || data?.codeExpiresAt) {
    return data.code_expires_at || data.codeExpiresAt;
  }
  const seconds = Number(data?.code_expires_in ?? data?.codeExpiresIn);
  if (Number.isFinite(seconds) && seconds > 0) {
    return new Date(Date.now() + seconds * 1000).toISOString();
  }
  // Fallback: 10 minutes (doc default) if backend omits expiry fields
  return new Date(Date.now() + 600 * 1000).toISOString();
};

export const extractMfaChallenge = (data, loginEmail = null) => ({
  token: data?.mfa_token || data?.mfaToken || null,
  phoneHint:
    data?.phone_hint ||
    data?.phoneHint ||
    data?.masked_phone ||
    data?.phone ||
    null,
  message: data?.message || data?.reason || data?.error || null,
  loginEmail: loginEmail || null,
  codeExpiresAt: resolveCodeExpiresAt(data),
  codeExpiresIn:
    data?.code_expires_in ?? data?.codeExpiresIn ?? null,
  mfaTokenExp: data?.mfa_token_exp || data?.mfaTokenExp || null,
  raw: data,
});

const mfaErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors) && data.errors[0]?.msg) {
    return data.errors.map((e) => e.msg).join(", ");
  }
  return (
    data?.reason ||
    data?.message ||
    data?.error ||
    error?.message ||
    fallback
  );
};

const initialState = {
  user: null,
  loading: false,
  error: null,
  loginError: null,
  lockedUntil: null,
  /** Pending SMS MFA after password login */
  mfa: null,
};

const getLoginErrorPayload = (error) => {
  const data = error?.response?.data || {};
  const message =
    (typeof data.error === "string" && data.error) ||
    (typeof data.message === "string" && data.message) ||
    (typeof data.error?.message === "string" && data.error.message) ||
    "Invalid Credentials";
  const lockedUntil = data.locked_until || data.lockedUntil || null;
  const isLocked =
    Boolean(lockedUntil) ||
    /lock/i.test(message) ||
    error?.response?.status === 423;

  return {
    message,
    lockedUntil,
    isLocked,
  };
};

export const registerUser = createAsyncThunk(
  "/auth/register",
  async (userData) => {
    try {
      const response = await API.post("/auth/register", userData);
      return response.data;
    } catch (error) {
      throw new Error("Failed to register user");
    }
  }
);

export const loginUser = createAsyncThunk(
  "/auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const loginEmail = credentials?.emailOrPhone;
      const deviceToken = getMfaDeviceToken(loginEmail);
      const payload = { ...credentials };
      if (deviceToken) {
        payload.mfa_device_token = deviceToken;
      }

      const response = await API.post("/auth/login", payload);
      const data = response.data;

      // SMS MFA challenge — do not treat as full session yet
      if (isMfaLoginChallenge(data)) {
        return {
          __mfa: true,
          challenge: extractMfaChallenge(data, loginEmail),
        };
      }

      // Trusted device / exempt — full session; keep any refreshed device token
      persistDeviceTokenFromSession(data, loginEmail);
      return data;
    } catch (error) {
      return rejectWithValue(getLoginErrorPayload(error));
    }
  }
);

/** Complete login after SMS OTP */
export const verifyLoginOtp = createAsyncThunk(
  "auth/verifyLoginOtp",
  async ({ otp, mfaToken, loginEmail }, { getState, rejectWithValue }) => {
    try {
      const email =
        loginEmail ||
        getState()?.auth?.mfa?.loginEmail ||
        null;
      const deviceToken = getMfaDeviceToken(email);
      const body = {
        mfa_token: mfaToken,
        code: String(otp).trim(),
      };
      if (deviceToken) {
        body.mfa_device_token = deviceToken;
      }

      const response = await API.post(MFA_VERIFY_PATH, body);
      const data = response.data;
      if (data?.access_token || data?.user) {
        persistDeviceTokenFromSession(data, email);
        showSuccess("Login Successfully!");
        return data;
      }
      const deniedMsg =
        data?.reason ||
        data?.message ||
        data?.error ||
        "OTP verification failed";
      showError(deniedMsg);
      return rejectWithValue({ message: deniedMsg });
    } catch (error) {
      const message = mfaErrorMessage(error, "Failed to verify OTP");
      const errorText = String(
        error.response?.data?.error ||
          error.response?.data?.message ||
          message ||
          ""
      );
      const sessionExpired =
        error.response?.status === 401 &&
        /session expired|sign in again/i.test(errorText);
      const codeExpired =
        error.response?.status === 401 &&
        /code expired|request a new code/i.test(errorText);
      showError(message);
      return rejectWithValue({
        message,
        status: error.response?.status,
        expired: sessionExpired,
        codeExpired,
      });
    }
  }
);

export const resendLoginOtp = createAsyncThunk(
  "auth/resendLoginOtp",
  async ({ mfaToken }, { getState, rejectWithValue }) => {
    try {
      const loginEmail = getState()?.auth?.mfa?.loginEmail || null;
      const response = await API.post(MFA_RESEND_PATH, {
        mfa_token: mfaToken,
      });
      showSuccess(
        response.data?.message || "OTP resent successfully"
      );
      return {
        challenge: extractMfaChallenge(
          {
            ...response.data,
            mfa_token: response.data?.mfa_token || mfaToken,
          },
          loginEmail
        ),
      };
    } catch (error) {
      const message = mfaErrorMessage(error, "Failed to resend OTP");
      const errorText = String(
        error.response?.data?.error ||
          error.response?.data?.message ||
          message ||
          ""
      );
      showError(message);
      return rejectWithValue({
        message,
        status: error.response?.status,
        expired:
          error.response?.status === 401 &&
          /session expired|sign in again/i.test(errorText),
      });
    }
  }
);

export const logoutUser = createAsyncThunk("/auth/logout", async () => {
  try {
    await API.delete("/auth/logout");
  } catch (error) {
    throw new Error("Failed to log out");
  }
});

export const registerUserData = createAsyncThunk(
  "/users/signup-data",
  async (signupcpde, { rejectWithValue }) => {
    try {
      const response = await API.post("/users/signup-data", signupcpde);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message:
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to get signup data",
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

export const refreshToken = createAsyncThunk(
  "/auth/refresh-token",
  async (accessToken) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.get("/auth/refresh-token", config);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearLoginError: (state) => {
      state.loginError = null;
      state.lockedUntil = null;
      state.error = null;
    },
    clearMfaChallenge: (state) => {
      state.mfa = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUserData.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.loginError = null;
        state.lockedUntil = null;
        state.error = null;
        state.mfa = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.__mfa) {
          state.mfa = action.payload.challenge;
          state.user = null;
          state.error = null;
          state.loginError = null;
          state.lockedUntil = null;
          return;
        }
        state.user = action.payload;
        state.mfa = null;
        state.error = null;
        state.loginError = null;
        state.lockedUntil = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        const message = payload.message || "Invalid Credentials";
        state.error = message;
        state.loginError = message;
        state.lockedUntil = payload.lockedUntil || null;
        state.mfa = null;
        showError(message);
      })
      .addCase(verifyLoginOtp.pending, (state) => {
        state.loading = true;
        state.loginError = null;
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.mfa = null;
        state.user = action.payload;
        state.error = null;
        state.loginError = null;
        state.lockedUntil = null;
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        state.error = payload.message || action.error.message;
        state.loginError = payload.message || null;
      })
      .addCase(resendLoginOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendLoginOtp.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.challenge?.token) {
          state.mfa = action.payload.challenge;
        }
        state.error = null;
      })
      .addCase(resendLoginOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
      })
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.mfa = null;
        state.error = null;
        state.loginError = null;
        state.lockedUntil = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        if (state.user) {
          state.user.access_token = action.payload?.access_token;
          state.user.exp = action.payload?.exp;
        }
      })
      .addCase(refreshToken.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.mfa = null;
        state.error = null;
      });
  },
});

export const { clearLoginError, clearMfaChallenge } = authSlice.actions;
export default authSlice.reducer;
